/**
 * Pure widget UI state machine. Not persisted anywhere — lives entirely in
 * the browser tab's React state, rebuilt fresh on every page load/widget
 * open.
 *
 * The bot menu (greeting_menu / faq_answer) has been removed — the widget
 * now goes straight from closed → connecting (when escalating to a live
 * agent) or closed → live_chat (when resuming an existing conversation).
 */
import type { BotOption } from './types.js'

export type WidgetStatus =
  | 'closed'
  | 'connecting'
  | 'escalation_failed'
  | 'live_chat'
  | 'ended'

export interface WidgetHistoryTurn {
  from: 'bot' | 'visitor'
  text: string
}

export interface WidgetState {
  status: WidgetStatus
  history: WidgetHistoryTurn[]
  options: BotOption[] | null
  conversationId: string | null
}

export const initialWidgetState: WidgetState = {
  status: 'closed',
  history: [],
  options: null,
  conversationId: null,
}

export type WidgetAction =
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'BOT_REPLY'; message: string; options?: BotOption[] }
  | { type: 'OPTION_PICKED'; label: string }
  | { type: 'ESCALATE' }
  | { type: 'AGENT_JOINED'; conversationId?: string }
  | { type: 'ESCALATION_TIMEOUT' }
  | { type: 'RETRY_ESCALATION' }
  | { type: 'CONVERSATION_ENDED' }

export function widgetReducer(state: WidgetState, action: WidgetAction): WidgetState {
  if (action.type === 'CLOSE') {
    return { ...state, status: 'closed', history: [], options: null }
  }

  switch (action.type) {
    case 'OPEN':
      // If we already have a conversationId (resuming), go straight to
      // live_chat. Otherwise go to connecting — the bot menu greeting is
      // no longer used.
      return {
        ...state,
        status: state.conversationId != null ? 'live_chat' : 'connecting',
      }
    case 'BOT_REPLY':
      // Bot replies are still appended to history (e.g. "Connecting you to
      // a live agent…") but no longer change the status — there is no
      // faq_answer state anymore.
      return {
        ...state,
        history: [...state.history, { from: 'bot', text: action.message }],
        options: action.options ?? null,
      }
    case 'OPTION_PICKED':
      return {
        ...state,
        history: [...state.history, { from: 'visitor', text: action.label }],
      }
    case 'ESCALATE':
      return { ...state, status: 'connecting', options: null }
    case 'AGENT_JOINED':
      return {
        ...state,
        status: 'live_chat',
        history: [...state.history, { from: 'bot', text: 'An agent has joined the conversation — you can chat now.' }],
        ...(action.conversationId !== undefined && { conversationId: action.conversationId }),
      }
    case 'ESCALATION_TIMEOUT':
      return {
        ...state,
        status: 'escalation_failed',
      }
    case 'RETRY_ESCALATION':
      return {
        ...state,
        status: 'connecting',
      }
    case 'CONVERSATION_ENDED':
      return { ...state, status: 'ended' }
    default:
      return state
  }
}
